/**
 * PlaylistTileHoverBodyV3 — Hover-card body for the Cover-Flow
 * Cinematic variation. The most cinematic of the five hover treatments.
 *
 * Layout (top-to-bottom):
 *   1. Hero banner — front-of-cycle thumbnail rendered full-width 16:9
 *      with a transparent → black gradient scrim at the bottom. Title
 *      and meta line overlay the bottom-left of the banner for an
 *      "iTunes album splash" feel.
 *   2. Bullet list of preview content titles (limit ~4 visible) —
 *      11/18 px, $body, with `· ` prefix.
 *   3. "+N more" line if itemCount exceeds shown titles.
 *   4. Full-width teal "View Playlist →" CTA pill (matches legacy +
 *      sibling variations).
 *
 * Banner thumbnail selection: `thumbnails[0]`. We don't have visibility
 * into the live front-of-cycle index (animation runs in CSS), and
 * since the user is hovering, the carousel is paused — pinning to the
 * first thumbnail is a stable, predictable choice. Empty array falls
 * back to the gradient backdrop with a `<StackedCards />` placeholder.
 */

import React from 'react'
import StackedCards from '../../../../Icon/icons/StackedCards/index'

const MAX_VISIBLE_TITLES = 4

interface PlaylistTileHoverBodyV3Props {
  playlistName: string
  metaLine: string
  thumbnails: string[]
  titles: string[]
  itemCount: number | null
}

function PlaylistTileHoverBodyV3 ({
  playlistName,
  metaLine,
  thumbnails,
  titles,
  itemCount,
}: PlaylistTileHoverBodyV3Props): React.ReactElement {
  const bannerThumbnail = thumbnails.length > 0 ? thumbnails[0] : null
  const shownTitles = titles.slice(0, MAX_VISIBLE_TITLES)
  const remaining = (itemCount ?? 0) - shownTitles.length

  return (
    <div className="playlist-search-tile-v3-hover">
      <div className="playlist-search-tile-v3-hover__banner">
        {bannerThumbnail !== null ? (
          <img
            src={bannerThumbnail}
            alt=""
            loading="lazy"
            className="playlist-search-tile-v3-hover__banner-img"
          />
        ) : (
          <div className="playlist-search-tile-v3-hover__banner-placeholder">
            <StackedCards size="40px" color="rgba(255, 255, 255, 0.65)" />
          </div>
        )}
        <div className="playlist-search-tile-v3-hover__banner-scrim" />
        <div className="playlist-search-tile-v3-hover__banner-text">
          <span className="playlist-search-tile-v3-hover__banner-name">
            {playlistName}
          </span>
          {metaLine && (
            <span className="playlist-search-tile-v3-hover__banner-meta">
              {metaLine}
            </span>
          )}
        </div>
      </div>

      <div className="playlist-search-tile-v3-hover__body">
        {shownTitles.length > 0 && (
          <ul className="playlist-search-tile-v3-hover__titles">
            {shownTitles.map((title, i) => (
              <li
                key={`${title}-${i}`}
                className="playlist-search-tile-v3-hover__title"
              >
                {title}
              </li>
            ))}
          </ul>
        )}

        {remaining > 0 && (
          <span className="playlist-search-tile-v3-hover__more">
            + {remaining} more
          </span>
        )}

        <span className="playlist-search-tile-v3-hover__cta">
          View Playlist &rarr;
        </span>
      </div>
    </div>
  )
}

export default PlaylistTileHoverBodyV3
